import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Menu, X, Bell, BellRing, User, MessageCircle, Trophy, LogOut, Home, ChevronDown, Building2, Shield, TrendingUp, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { registerPushNotifications, isPushEnabled } from '../utils/pushNotifications';
import { useTheme } from '../context/ThemeContext';
import InstallPWA from './InstallPWA';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_665a114b-bc62-4112-9e11-8ccdfcfc0d6e/artifacts/auplmu5t_1774296304598.png';

const Navbar = () => {
  const { isAuthenticated, user, logout, token } = useAuth();
  const { t, language, changeLanguage, languageLabels } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchNotifications();
      isPushEnabled().then(setPushEnabled);
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  const handleEnablePush = async () => {
    const result = await registerPushNotifications(token);
    if (result) {
      setPushEnabled(true);
      toast.success(language === 'it' ? 'Notifiche push attivate!' : 'Push notifications enabled!');
    } else {
      toast.error(language === 'it' ? 'Impossibile attivare le notifiche push' : 'Could not enable push notifications');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch { /* ignore */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notif.notification_id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    // Navigate to link
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/feed', label: t('feed') },
    { path: '/marketplace', label: language === 'it' ? 'Mercatino' : 'Marketplace' },
    { path: '/donate-time', label: language === 'it' ? 'Dona Tempo' : 'Donate Time' },
    { path: '/heart-desires', label: language === 'it' ? 'Desideri' : 'Wishes' },
    { path: '/leaderboard', label: t('leaderboard') },
    ...(isAuthenticated ? [
      { path: '/corporate-fund', label: t('corporateFund') },
      { path: '/chat', label: t('chat') }
    ] : [
      { path: '/corporate-fund', label: t('corporateFund') }
    ])
  ];

  return (
    <nav className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700 sticky top-0 z-50 backdrop-blur-lg bg-white/95 dark:bg-stone-900/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Moneyfor" className="h-8 w-8 dark:brightness-110" style={{mixBlendMode: 'multiply'}} />
            <span className="font-heading text-xl font-bold text-stone-900 dark:text-white group-hover:text-amber-600 transition-colors">
              Moneyfor
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-amber-700 bg-amber-50' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm hover:bg-stone-50 transition-colors" data-testid="language-selector">
                  <span>{languageLabels[language]?.flag}</span>
                  <span className="hidden sm:inline text-stone-600 font-medium">{languageLabels[language]?.short}</span>
                  <ChevronDown className="h-3 w-3 text-stone-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {Object.entries(languageLabels).map(([code, info]) => (
                  <DropdownMenuItem key={code} onClick={() => changeLanguage(code)}
                    className={language === code ? 'bg-amber-50 text-amber-700' : ''}>
                    <span className="mr-2">{info.flag}</span>{info.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Install PWA Button */}
            <InstallPWA />

            {/* Theme Toggle - always visible */}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle"
              className="text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700">
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {isAuthenticated ? (
              <>
                {/* Notifications */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                      <Bell className="h-5 w-5 text-stone-600" />
                      {unreadCount > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80">
                    <div className="p-3 font-semibold border-b flex items-center justify-between">
                      <span>{t('notifications')}</span>
                      <div className="flex items-center gap-2">
                        {!pushEnabled && (
                          <button onClick={handleEnablePush} data-testid="enable-push-btn"
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                            <BellRing className="h-3 w-3" />
                            {language === 'it' ? 'Attiva push' : 'Enable push'}
                          </button>
                        )}
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-amber-600 hover:text-amber-700 font-medium">
                            {language === 'it' ? 'Segna letto' : 'Mark read'}
                          </button>
                        )}
                      </div>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-stone-500">{t('noNotifications')}</div>
                    ) : (
                      notifications.slice(0, 5).map((notif) => (
                        <DropdownMenuItem key={notif.notification_id} className="p-3 cursor-pointer"
                          onClick={() => handleNotificationClick(notif)}>
                          <div>
                            <p className={`font-medium ${notif.is_read ? 'text-stone-500' : 'text-stone-900'}`}>{notif.title}</p>
                            <p className="text-sm text-stone-500 truncate">{notif.message}</p>
                          </div>
                          {!notif.is_read && <div className="w-2 h-2 bg-amber-500 rounded-full ml-auto shrink-0" />}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-1 rounded-full hover:bg-stone-50 transition-colors">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user?.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${user?.user_id}`} />
                        <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <ChevronDown className="h-4 w-4 text-stone-400 hidden md:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="font-semibold text-stone-900">{user?.name}</p>
                      <p className="text-sm text-stone-500">{user?.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2">
                        <User className="h-4 w-4" />{t('profile')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-dashboard-link">
                        <TrendingUp className="h-4 w-4" />{language === 'it' ? 'La mia attività' : 'My Activity'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />{t('settings')}
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 text-amber-700" data-testid="nav-admin-link">
                            <Shield className="h-4 w-4" />Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />{t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login-btn" className="text-stone-700">{t('login')}</Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="nav-register-btn" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-5">
                    {t('register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 text-stone-600" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-sm font-medium ${
                  isActive(link.path) ? 'text-amber-700 bg-amber-50' : 'text-stone-600 hover:bg-stone-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && (
              <div className="pt-3 space-y-2">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-xl">{t('login')}</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl">{t('register')}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
